import type { FetchResponse } from 'openapi-fetch'
import type { MediaType } from 'openapi-typescript-helpers'
import type { components } from '@@/api/schema'
import { createApiClient, type Client } from '@@/api/client.js'
import { useAuth } from '@/composables/useAuth.js'
import { usePreventNavigation } from '@/composables/usePreventNavigation.js'

let client: Client

export interface APIError {
  result?: {
    error?: {
      errors?: components['schemas']['errors']
    }
  }
}

type APICall<
  T extends Record<string | number, unknown>,
  Options = unknown,
  Media extends MediaType = MediaType,
> = (client: Client) => Promise<FetchResponse<T, Options, Media>>

function isUnauthenticated(response: Response): boolean {
  return (
    response.status === 403 &&
    Boolean(response.headers.get('x-provet-cloud-not-authenticated'))
  )
}

type ApiErrorItem = { detail?: string; title?: string }
type ApiErrorPayload = { errors: ApiErrorItem[] }

function hasErrors(error: unknown): error is ApiErrorPayload {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as { errors?: unknown }).errors)
  )
}

function formatApiErrorPayload(error: unknown): string {
  if (error === null || error === undefined) {
    return 'Request failed'
  }
  if (typeof error === 'string') {
    return error
  }
  if (hasErrors(error)) {
    const parts = error.errors
      .map((e) => e.detail || e.title)
      .filter((s): s is string => Boolean(s))
    if (parts.length > 0) {
      return parts.join('; ')
    }
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }
  try {
    return JSON.stringify(error)
  } catch {
    return 'Unknown error'
  }
}

export class ApiError<
  T extends Record<string | number, unknown>,
  Options = unknown,
  Media extends MediaType = MediaType,
> extends Error {
  override name = 'ApiError'

  /** HTTP status from the response (for Nuxt error handling / dev overlay). */
  readonly statusCode: number

  constructor(public readonly result: FetchResponse<T, Options, Media>) {
    const message = `${formatApiErrorPayload(result.error)} (${result.response.status}: ${result.response.statusText})`
    const error = result.error
    const firstError = hasErrors(error)
      ? (error as ApiErrorPayload).errors[0]
      : undefined
    super(message, {
      cause: firstError?.detail ?? firstError?.title,
    })
    this.statusCode = result.response.status
  }

  is(status: number) {
    return this.result.response.status === status
  }
}

/**
 * Gets a reference to the API client. Some indirection is necessary,
 * so that we can throw on 4xx/5xx response status, whilst keeping
 * everything strongly typed.
 *
 * @example
 * const api = useApi()
 * const response = await api(client => client.GET("/{provet_id}/api/users", {
 *   params: {
 *     path: { provet_id: "123" },
 *     query: { "page[number]": 2 }
 *   },
 * }))
 */
export function useApi() {
  const config = useRuntimeConfig()
  const { setShouldPreventNavigation } = usePreventNavigation()

  client ??= createApiClient({
    baseUrl: config.public.apiBaseUrl,
    afterResponse: (response) => {
      if (isUnauthenticated(response)) {
        const { forceLogin } = useAuth()
        const route = useRoute()

        // When authentication expires, bypass navigation confirmation dialogs
        // to allow direct redirection to the login page without interruption
        setShouldPreventNavigation(false)
        return forceLogin(route)
      }
    },
  })

  return async function <
    T extends Record<string | number, unknown>,
    Options = unknown,
    Media extends MediaType = MediaType,
    TTransformed = NonNullable<FetchResponse<T, Options, Media>['data']>,
  >(
    apiCall: APICall<T, Options, Media>,
    transform?: (
      data: NonNullable<FetchResponse<T, Options, Media>>,
    ) => TTransformed,
  ): Promise<TTransformed> {
    const response = await apiCall(client)

    if (response.error) {
      throw new ApiError(response)
    }

    return transform ? transform(response) : (response.data! as TTransformed)
  }
}
